import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const EngagementTable = ({ data, title, columns, type = 'pages' }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig?.key === key && sortConfig?.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig?.key) return data;
    
    return [...data]?.sort((a, b) => {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig?.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return sortConfig?.direction === 'asc' 
        ? String(aValue)?.localeCompare(String(bValue))
        : String(bValue)?.localeCompare(String(aValue));
    });
  }, [data, sortConfig]);

  const paginatedData = sortedData?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(sortedData?.length / itemsPerPage);

  const getSortIcon = (key) => {
    if (sortConfig?.key !== key) return 'ChevronsUpDown';
    return sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown';
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-sm overflow-hidden">
      <div className="p-4 md:p-6 border-b border-border">
        <h3 className="text-base md:text-lg font-semibold text-foreground">{title}</h3>
        <p className="text-xs md:text-sm text-muted-foreground mt-1">
          {type === 'pages' ? 'Most viewed catalog pages' : type === 'hotspots' ? 'Most clicked interactive elements' : 'Traffic source breakdown'}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              {columns?.map((column) => (
                <th
                  key={column?.key}
                  onClick={() => column?.sortable && handleSort(column?.key)}
                  className={`px-4 md:px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider ${column?.sortable ? 'cursor-pointer hover:bg-muted transition-colors' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <span>{column?.label}</span>
                    {column?.sortable && (
                      <Icon name={getSortIcon(column?.key)} size={14} className="text-muted-foreground" />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginatedData?.map((row, index) => (
              <tr key={index} className="hover:bg-muted/30 transition-colors">
                {columns?.map((column) => (
                  <td key={column?.key} className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap">
                    {column?.render ? column?.render(row?.[column?.key], row) : (
                      <span className="text-sm text-foreground">{row?.[column?.key]}</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="px-4 md:px-6 py-3 md:py-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs md:text-sm text-muted-foreground">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedData?.length)} of {sortedData?.length} entries
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Icon name="ChevronLeft" size={16} />
            </button>
            <span className="text-sm text-foreground px-3">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Icon name="ChevronRight" size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EngagementTable;